import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createBannerApi,
  getBannerApi,
  deleteBannerApi,
  getRestaurantBannerApi,
} from "../../api/bannerApi";

// ==============================
// CREATE BANNER
// ==============================

export const createBanner = createAsyncThunk(
  "banner/createBanner",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await createBannerApi(formData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload banner"
      );
    }
  }
);

// ==============================
// GET BANNER
// ==============================

export const getBanner = createAsyncThunk(
  "banner/getBanner",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const { data } = await getBannerApi(restaurantId);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch banners"
      );
    }
  }
);

// ==============================
// DELETE BANNER
// ==============================

export const deleteBanner = createAsyncThunk(
  "banner/deleteBanner",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await deleteBannerApi(id);
      return { id, message: data.message };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete banner"
      );
    }
  }
);

export const getRestaurantBanner = createAsyncThunk(
  "banner/getRestaurantBanner",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getRestaurantBannerApi();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurant banners"
      );
    }
  }
);
// ==============================
// SLICE
// ==============================

const bannerSlice = createSlice({
  name: "banner",
  initialState: {
    banners: [],
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    clearBannerState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // ================= CREATE =================
      .addCase(createBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.banners = [...action.payload.images, ...state.banners];
      })
      .addCase(createBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ================= GET =================
      .addCase(getBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = action.payload.banner;
      })
      .addCase(getBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ================= DELETE =================
      .addCase(deleteBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = state.banners.filter(
          (item) => item._id !== action.payload.id
        );
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ================= GET RESTAURANT BANNER =================
      .addCase(getRestaurantBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRestaurantBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = action.payload.banner;
      })
      .addCase(getRestaurantBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },
});

export const { clearBannerState } = bannerSlice.actions;
export default bannerSlice.reducer;
